// src/services/authService.js
// 웹 authService.js와 동일한 Firestore 필드 구조 사용
// nickname, points, isAdmin, managedStores, gender, birth, provider, createdAt(ISO string)
// 50차: registerWithEmail — 약관 동의 이력(agreements 필드 + agreements 컬렉션) 저장
// 54차: 소셜 신규가입 공통 핸들러(ensureSocialUserDoc) 추가
//        - users 문서 생성은 클라(isOwner 허용), points: 0 으로 생성
//        - 신규가입 500P 지급은 grantSignupPoints(→ CF grantSignupPointsCF) 단일 경로로 통일
//        - 클라 직접 pointHistory addDoc / points:500 박기 제거 (53차 Rules 정합)
//        - Apple / Google 모두 이 핸들러를 통해 신규가입 처리
// 74차: 이메일 회원가입 시 인증 메일 발송(sendEmailVerification) 추가 — 지금까지 이메일
//        검증 절차가 아예 없어 존재하지 않는/타인 이메일로도 가입이 됐음. 메일 발송
//        실패는 가입 자체를 막지 않도록 무음 처리(네트워크 문제로 가입 자체가 막히면 안 됨).
//        resendEmailVerification 신규 — 마이페이지에서 재전송용.
//        SNS 로그인(카카오/네이버/구글/애플) 계정은 각 provider가 이미 검증한 이메일이라
//        별도 인증 절차를 추가하지 않음.
// 75차: 데드코드 정리 — updateNickname/changePassword 삭제. EditProfileScreen.js가
// 이 함수들을 거치지 않고 동일 로직을 직접 구현해서 쓰고 있어(중복), 실제로는 아무도
// 호출하지 않는 코드였음.
// 76차: loginWithEmail — 입력값에 "@"가 없으면 매장관리자 ID로 간주해 내부 합성
// 이메일(STORE_ADMIN_ID_DOMAIN)로 변환 후 로그인. 신규 매장관리자 계정은
// createStoreAdminAccountCF(functions/index.js)가 이 도메인으로 Auth 계정을 생성함.
// changePasswordWithReauth 신규 — EditProfileScreen.js의 인라인 로직을 추출, 강제
// 비밀번호 변경 화면(ForcedPasswordChangeScreen)에서도 재사용.
// 78차: 데드코드 정리 — grantReviewPoint 삭제(호출부 0곳, 리뷰 포인트는 usePointsFIFO/CF
// 경로로 처리됨). 이로 인해 미사용이 된 updateDoc import도 함께 제거.
// 78차 32차: deleteAccount 신규 — 회원 탈퇴. 실제 삭제/익명화는 Admin SDK로 처리하는
// deleteAccountCF(functions/index.js)가 전담하고, 여기서는 호출 + 로컬 세션 정리만.

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import {
  doc, getDoc,
  collection, writeBatch,
  disableNetwork, enableNetwork,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { auth, db, functions } from './firebase';
import { grantSignupPoints, applyReferral } from './signupPointHelper';
import { unregisterPushToken } from './notificationService';

// ─── 닉네임 예약어 체크 ─────────────────────────────────────
const RESERVED_NICKNAMES = ['👑 총관리자 👑', '총관리자', 'superadmin', 'SUPERADMIN'];

// ─── 약관 버전 (약관 변경 시 이 값을 올려서 재동의 유도 가능) ──
const TERMS_VERSION = '1.0';

// ─── 회원가입 ────────────────────────────────────────────────
// 웹 signUp()과 동일한 로직 + 필드
// 50차: agreedAt 파라미터 추가 — 약관 동의 이력 저장
export const registerWithEmail = async ({ email, password, nickname, agreedAt, inviterUid }) => {
  if (RESERVED_NICKNAMES.some((r) => nickname.trim() === r)) {
    throw new Error('사용할 수 없는 닉네임이에요.');
  }

  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(user, { displayName: nickname });
  await sendEmailVerification(user).catch(() => {}); // 74차: 인증 메일 발송 실패는 가입을 막지 않음

  const now = agreedAt || new Date().toISOString();
  const agreementData = {
    terms:   { agreed: true, agreedAt: now, version: TERMS_VERSION },
    privacy: { agreed: true, agreedAt: now, version: TERMS_VERSION },
  };

  // 79차: [버그 수정] users 문서 생성 + agreements 기록을 각각 별개의 await로 처리하고
  // 있었음 — ensureSocialUserDoc(SNS 가입)은 78차에 이미 정확히 이 이유로 writeBatch로
  // 원자화됐는데("계정은 이미 생겼는데 agreements만 실패하면 반쪽 상태가 되고, 그게
  // 카카오 가입 후 '자동 로그인 안 됨' 신고의 실제 원인이었다"는 기록이 남아있음),
  // 이메일 가입 경로엔 그 교훈이 반영 안 된 채 원래의 비원자적 구조 그대로 있었음
  // (전수 정독으로 발견) — users 문서 생성 후 agreements addDoc이 네트워크 장애 등으로
  // 실패하면 Auth 계정+Firestore 프로필은 이미 만들어졌는데 화면엔 "가입 실패"로 뜨고,
  // 이후 같은 이메일로 재가입 시도 시 auth/email-already-in-use로 혼란만 커짐. 웹과
  // 동일한 Firestore 문서 구조 + agreements 필드.
  // 54차: points 는 0 으로 생성 (500P 는 CF grantSignupPointsCF 가 서버에서 지급)
  const batch = writeBatch(db);
  batch.set(doc(db, 'users', user.uid), {
    uid: user.uid,
    email,
    nickname,
    gender: '',
    birth: '',
    isAdmin: false,
    adminRole: null,
    managedStores: [],
    points: 0,                            // 54차: CF 가 지급 (클라 직접 500 박기 제거)
    createdAt: new Date().toISOString(),  // 웹: ISO string
    provider: 'email',
    agreements: agreementData,            // 50차: 약관 동의 이력
  });
  // 50차: 별도 agreements 컬렉션에 법적 증거용 기록
  batch.set(doc(collection(db, 'agreements')), {
    uid:      user.uid,
    type:     'email_registration',
    terms:    true,
    privacy:  true,
    agreedAt: now,
    version:  TERMS_VERSION,
    platform: 'mobile',
    provider: 'email',
  });
  await batch.commit();

  // 54차: 신규가입 500P — CF 단일 경로 (클라 직접 pointHistory write 제거, 53차 Rules 정합)
  await grantSignupPoints(user.uid);
  // 80차 계속: 친구 초대 리워드(선택) — 코드 입력 안 했으면 applyReferral 내부에서 즉시 무시.
  await applyReferral(inviterUid);

  return user;
};

// ─── 소셜 신규가입 공통 핸들러 ──────────────────────────────
// 54차: Apple / Google 등 모든 소셜 로그인의 신규가입 단일 진입점.
//   - 이미 users 문서가 있으면(기존 유저) 아무것도 안 함 → false 반환
//   - 신규면 users 문서 생성(points:0) + agreements 기록 + 500P CF 지급 → true 반환
//   - displayName 은 provider 가 주는 이름 우선, 없으면 '탐험가####' 폴백
//   - email 은 provider 가 안 줄 수도 있음(Apple 2회차 로그인 등) → 빈 문자열 허용
export const ensureSocialUserDoc = async ({
  uid,
  email = '',
  displayName = '',
  photoURL = '',
  provider,          // 'apple' | 'google' | 'kakao' | 'naver'
  agreedAt,
  inviterUid,
}) => {
  const ref  = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  // 80차 후속: [버그 수정, 중간 심각도 — fork로 발견] 카카오/네이버는 provider ID로
  // 결정론적 uid를 써서, 탈퇴(deleteAccountCF) 후 같은 SNS 계정으로 재가입하면 정확히
  // 같은 uid의 익명화된(isDeleted:true) 유령 문서가 이미 있다는 이유만으로 신규가입
  // 처리를 건너뛰고 있었음 — LoginScreen.js도 이 문서를 "기존 유저"로 취급해 온보딩
  // 없이 그대로 로그인시켜, 탈퇴가 사실상 되돌려지지 않는 깨진 상태로 남았음. isDeleted
  // 문서는 계속 신규가입 취급(아래에서 부활 처리).
  const isRevival = snap.exists() && snap.data().isDeleted === true;
  if (snap.exists() && !isRevival) return false;  // 진짜 기존 유저 — 신규가입 처리 안 함

  // 79차: [버그 수정] registerWithEmail(이메일 가입)은 RESERVED_NICKNAMES 재검증이
  // 있는데 이 함수(SNS 가입)엔 전혀 없어서, SocialOnboardingScreen.js의 화면단 검증
  // (BLOCKED_NICKNAME_WORDS)을 거치지 않고 이 함수를 직접 호출하면(둘 다 순수 클라
  // 함수라 firestore.rules/CF 보호 없음) "관리자" 등 예약어 닉네임으로 SNS 가입이
  // 가능했음(전수 정독으로 발견) — 형제 함수와 동일한 방어선 추가.
  const socialNickname = (displayName && displayName.trim()) ? displayName.trim() : '';
  if (socialNickname && RESERVED_NICKNAMES.some((r) => socialNickname === r)) {
    throw new Error('사용할 수 없는 닉네임이에요.');
  }

  const nowStr = agreedAt || new Date().toISOString();
  const agreementData = {
    terms:   { agreed: true, agreedAt: nowStr, version: TERMS_VERSION },
    privacy: { agreed: true, agreedAt: nowStr, version: TERMS_VERSION },
  };
  const nickname = (displayName && displayName.trim())
    ? displayName.trim()
    : '탐험가' + Math.floor(Math.random() * 9999);

  // 78차: users 문서 생성 + agreements 기록을 writeBatch로 원자화 — 예전엔 두 번의
  // 별개 요청이라 users 문서만 만들어진 뒤 agreements addDoc이 실패하면(네트워크 등)
  // "계정은 이미 생겼는데 이 화면은 실패로 뜨는" 반쪽 상태가 됐음. SocialOnboardingScreen은
  // 이 에러를 그냥 Alert로만 보여주고 authRedirectSuppressed를 안 풀어서, 사용자가 로그인을
  // 재시도하거나 앱을 재시작해야만(그때는 이미 존재하는 계정으로 인식돼) 홈으로 넘어갈 수
  // 있었던 것 — 카카오 회원가입 후 "자동 로그인/홈 이동 안 됨" 신고의 실제 원인.
  const batch = writeBatch(db);
  if (isRevival) {
    // 탈퇴 유령 부활 — points/adminRole/managedStores 등 보호 필드는 건드리지 않음
    // (탈퇴 시점에 이미 안전한 값으로 초기화돼 있고, 규칙상 자기 자신도 이 필드들은
    // update로 못 바꿈). 프로필 관련 필드만 새로 부여받은 값으로 되살림.
    batch.update(ref, {
      isDeleted: false,
      deletedAt: null,
      email: email || '',
      nickname,
      photoURL: photoURL || '',
      agreements: agreementData,
    });
  } else {
    batch.set(ref, {
      uid,
      email: email || '',
      nickname,
      gender: '',
      birth: '',
      isAdmin: false,
      adminRole: null,
      managedStores: [],
      points: 0,
      photoURL: photoURL || '',
      createdAt: nowStr,
      provider,
      agreements: agreementData,
    });
  }
  batch.set(doc(collection(db, 'agreements')), {
    uid,
    type:     `${provider}_registration`,
    terms:    true,
    privacy:  true,
    agreedAt: nowStr,
    version:  TERMS_VERSION,
    platform: 'mobile',
    provider,
  });
  await batch.commit();

  // 신규가입 500P — CF 단일 경로 (53차 보안 이관 정합). grantSignupPoints는 내부적으로
  // 모든 실패를 무음 처리하므로 여기서 던져지지 않음 — 위 batch.commit()이 성공한 이상
  // 계정 생성 자체는 항상 완료된 상태로 반환된다.
  await grantSignupPoints(uid);
  // 80차 계속: 친구 초대 리워드(선택) — SocialOnboardingScreen.js에서 입력받은 코드.
  await applyReferral(inviterUid);

  return true;
};

// ─── 이메일 로그인 ──────────────────────────────────────────
// 웹 signIn()과 동일
// ⚠️ users 문서 존재 여부 검증 제거:
//   - 관리자 계정처럼 users 문서가 없거나 pendingAdmins로만 등록된 경우도 로그인 가능해야 함
//   - 프로필 조회 및 pendingAdmins 병합은 AuthContext.onAuthStateChanged에서 처리
// 76차: "@" 없는 입력은 매장관리자 ID로 간주해 내부 합성 이메일로 변환.
//   이 도메인 문자열은 functions/index.js의 createStoreAdminAccountCF와 반드시 동일해야 함.
const STORE_ADMIN_ID_DOMAIN = '@store.unlockapp.internal';

export const loginWithEmail = async ({ email, password }) => {
  const input = email.trim();
  const loginEmail = input.includes('@') ? input : `${input}${STORE_ADMIN_ID_DOMAIN}`;
  const { user } = await signInWithEmailAndPassword(auth, loginEmail, password);
  return user;
};

// ─── 로그아웃 ───────────────────────────────────────────────
// 78차: "로그아웃 직후 홈 화면이 계속 로딩(스켈레톤)에 멈춰있다"는 반복 신고 — 이전
// 라운드의 방어적 타임아웃(HomeScreen.js)은 증상만 10초 뒤에 풀어줄 뿐 재현이 안 됐고
// 사용자가 재발을 신고함. 이 앱은 firebase/firestore JS SDK를 그대로 React Native에서
// 쓰는데(네이티브 @react-native-firebase가 아님), RN엔 브라우저의 grpc streaming이 없어
// Firestore SDK가 모든 요청(실시간 리스너뿐 아니라 getDocs 같은 1회성 조회까지)을 하나의
// long-polling 채널로 처리한다 — signOut() 시점에 그 채널이 이전 자격증명을 낀 채로 열려
// 있으면, 채널이 새 자격증명(비로그인)으로 재구성되는 과정에서 마침 그 순간 진행 중이던
// 요청이 응답도 에러도 없이 채널과 함께 고아 상태로 멈춰버릴 수 있음(firebase-js-sdk의
// 알려진 RN 환경 이슈) — 로그아웃 직후 재마운트되는 게스트 홈 화면의 getDocs가 바로 이
// 타이밍에 걸리면 영구히 멈춘 것처럼 보임. signOut 직후 Firestore 네트워크를 명시적으로
// 껐다 켜서(disableNetwork→enableNetwork) 채널을 강제로 새로 열게 하면, 고아가 될 수
// 있었던 요청 자체가 발생하지 않는다.
export const logout = async () => {
  // 79차: signOut() 이후엔 auth.currentUser가 사라져 users 문서에 못 쓰므로 그 전에 정리.
  if (auth.currentUser) {
    await unregisterPushToken(auth.currentUser.uid);
  }
  // 80차 후속: Android 네이티브 Google Sign-In(signInWithGoogleNative)이 기기에 세션을
  // 캐시해서, 이걸 안 지우면 로그아웃 후 다시 "Google로 계속하기"를 눌러도 계정 선택
  // 화면 없이 같은 계정으로 조용히 재로그인돼 다른 계정으로 전환할 방법이 없었음
  // (Kakao/Naver는 이런 네이티브 세션 캐시가 없어 기존엔 해당 없었음).
  // 오늘 fork 재검토로 발견/수정: 이 파일 최상단의 정적 import { GoogleSignin }만으로도
  // Expo Go가 앱 시작 즉시 크래시했음(socialAuthService.js와 동일 원인/수정 — require를
  // 실제 호출 시점까지 지연).
  // 오늘 재검토(순차 기기테스트)로 추가 발견/수정: require()를 지연시켜도, Expo Go에서
  // 라이브러리 내부의 TurboModuleRegistry.getEnforcing()이 try/catch로 잡히는 JS 예외와
  // 별개로 RN 개발모드 redbox를 띄워서 로그아웃이 사실상 막혔음(실제 EAS 빌드는 네이티브
  // 모듈이 링크돼 있어 해당 없음) — LoginScreen.js가 이미 SNS 로그인 시 쓰는 것과 동일한
  // Constants.appOwnership==='expo' 감지로, Expo Go에서는 아예 호출 자체를 건너뜀(Expo
  // Go 안에서는 애초에 구글 네이티브 로그인이 성립할 수 없어 지울 세션도 없음).
  if (Platform.OS === 'android' && Constants.appOwnership !== 'expo') {
    try {
      const { GoogleSignin } = require('@react-native-google-signin/google-signin');
      await GoogleSignin.signOut();
    } catch { /* 구글로 로그인한 적 없으면 무해하게 실패 */ }
  }
  await signOut(auth);
  try {
    await disableNetwork(db);
    await enableNetwork(db);
  } catch {
    // 네트워크 사이클 자체가 실패해도 로그아웃은 이미 끝난 상태 — 무음 처리
  }
};

// ─── 비밀번호 재설정 이메일 ─────────────────────────────────
export const sendPasswordReset = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

// ─── 74차: 이메일 인증 메일 재전송 ───────────────────────────
export const resendEmailVerification = async () => {
  if (!auth.currentUser) throw new Error('로그인이 필요합니다.');
  await sendEmailVerification(auth.currentUser);
};

// ─── 76차: 비밀번호 변경(재인증 포함) ─────────────────────────
// EditProfileScreen.js에 인라인으로만 있던 로직을 추출 — 강제 비밀번호 변경 화면
// (ForcedPasswordChangeScreen, 매장관리자 최초 로그인용)에서도 동일하게 재사용한다.
export const changePasswordWithReauth = async ({ email, currentPassword, newPassword }) => {
  const cred = EmailAuthProvider.credential(email, currentPassword);
  await reauthenticateWithCredential(auth.currentUser, cred);
  await updatePassword(auth.currentUser, newPassword);
};

// ─── 78차 32차: 회원 탈퇴 ─────────────────────────────────────
export const deleteAccount = async () => {
  const cf = httpsCallable(functions, 'deleteAccountCF');
  await cf();
  await logout();
};

// ─── 유저 프로필 조회 ────────────────────────────────────────
// 웹 getUserData()와 동일
export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
};
